import { useState } from "react";
import { useForm } from "react-hook-form";
import InputField from "../../components/ui/InputField";
import { useToast } from "../../hooks/useToast";
import { LuShieldAlert, LuKey } from "react-icons/lu";

interface SecurityForm {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export default function SecurityTab() {
    const { register, handleSubmit, watch, reset, formState: { errors, isDirty } } = useForm<SecurityForm>({
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
    });

    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    const onSubmit = async () => {
        setIsLoading(true);
        try {
            // simulate API call
            await new Promise((r) => setTimeout(r, 1000));

            showToast({
                title: "Password Updated",
                message: "Your password has been updated successfully.",
                variant: "success",
                duration: 3000,
            });

            reset();
        } catch {
            showToast({
                title: "Update Failed",
                message: "Unable to update password. Please try again.",
                variant: "error",
                duration: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const newPasswordValue = watch("newPassword");

    return (
        <div className="space-y-8">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">Password & Security</h3>
                    <p className="text-gray-500 text-sm">Manage your password and secure your account.</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center shrink-0">
                    <LuShieldAlert className="text-amber-500" size={24} />
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 pt-4 border-t border-gray-100">
                <div className="max-w-xl space-y-6">
                    <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                            Current Password
                            <a href="#" className="text-violet-600 hover:text-violet-700 normal-case tracking-normal hover:underline">Forgot password?</a>
                        </label>
                        <InputField
                            type="password"
                            placeholder="Enter your current password"
                            disabled={isLoading}
                            className="bg-gray-50 border-gray-200 focus:border-violet-500 py-3 rounded-xl"
                            error={errors.currentPassword?.message}
                            {...register("currentPassword", { required: "Current password is required" })}
                        />
                    </div>

                    <div className="pt-4 space-y-6 relative">
                        <div className="absolute left-0 top-0 w-full h-px bg-gray-100"></div>
                        <div>
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                                New Password
                            </label>
                            <InputField
                                type="password"
                                placeholder="8+ characters"
                                disabled={isLoading}
                                className="bg-gray-50 border-gray-200 focus:border-violet-500 py-3 rounded-xl"
                                error={errors.newPassword?.message}
                                {...register("newPassword", {
                                    required: "New password is required",
                                    minLength: { value: 8, message: "Password must be at least 8 characters" },
                                })}
                            />
                            {/* Password strength indicator mock */}
                            {newPasswordValue && (
                                <div className="flex gap-1 mt-3">
                                    <div className={`h-1.5 flex-1 rounded-full ${newPasswordValue.length > 0 ? 'bg-red-400' : 'bg-gray-200'}`}></div>
                                    <div className={`h-1.5 flex-1 rounded-full ${newPasswordValue.length >= 6 ? 'bg-amber-400' : 'bg-gray-200'}`}></div>
                                    <div className={`h-1.5 flex-1 rounded-full ${newPasswordValue.length >= 8 ? 'bg-emerald-400' : 'bg-gray-200'}`}></div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                                Confirm New Password
                            </label>
                            <InputField
                                type="password"
                                placeholder="Match your new password"
                                disabled={isLoading}
                                className="bg-gray-50 border-gray-200 focus:border-emerald-500 py-3 rounded-xl"
                                error={errors.confirmPassword?.message}
                                {...register("confirmPassword", {
                                    required: "Please confirm your new password",
                                    validate: (val) => val === newPasswordValue || "Passwords do not match",
                                })}
                            />
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4 pt-6 mt-8 border-t border-gray-100">
                    <button 
                        type="submit" 
                        disabled={isLoading || !isDirty}
                        className="px-6 py-2.5 rounded-xl text-sm font-bold bg-gray-900 text-white shadow-lg shadow-gray-200 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 transition-all flex items-center gap-2"
                    >
                        {isLoading ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <LuKey size={16} />
                        )}
                        Change Password
                    </button>
                    {isDirty && (
                        <button
                            type="button"
                            onClick={() => reset()}
                            className="px-4 py-2.5 text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}
